import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Shield, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard', desc: 'Main dashboard & stats' },
  { key: 'leads', label: 'Leads', desc: 'Lead management & pipeline' },
  { key: 'calendar', label: 'Calendar', desc: 'Appointments & scheduling' },
  { key: 'messages', label: 'Messages', desc: 'Direct messaging' },
  { key: 'email', label: 'Email', desc: 'Email integration & sequences' },
  { key: 'groups', label: 'Groups', desc: 'Private groups' },
  { key: 'members', label: 'Members', desc: 'Team member directory' },
  { key: 'reports', label: 'Reports', desc: 'Lead aging & reports' },
  { key: 'analytics', label: 'Analytics', desc: 'Admin analytics & A/B tests' },
  { key: 'campaigns', label: 'Campaigns', desc: 'Email campaigns' },
  { key: 'rules', label: 'Assignment Rules', desc: 'Auto lead assignment' },
  { key: 'webhooks', label: 'Webhooks', desc: 'Webhook integrations' },
  { key: 'support', label: 'Support', desc: 'Support chat dashboard' },
  { key: 'billing', label: 'Billing', desc: 'Payments & subscriptions' },
];

export default function PermissionsDialog({ open, onOpenChange, member, currentUserRole }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPerms = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    try {
      const { data } = await api.getMemberPermissions(member.id);
      setPermissions(data.module_permissions || ALL_MODULES.map(m => m.key));
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [member?.id]);

  useEffect(() => { if (open) fetchPerms(); }, [open, fetchPerms]);

  const toggle = (key) => {
    setPermissions(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => setPermissions(ALL_MODULES.map(m => m.key));
  const clearAll = () => setPermissions([]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.setMemberPermissions(member.id, permissions);
      toast.success(`Permissions updated for ${member.name}`);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  // Filter which modules can be assigned based on the assigner's role
  const canAssignAdmin = currentUserRole === 'super_admin';
  const targetRole = member?.role || 'member';
  const availableModules = ALL_MODULES.filter(m => {
    // Admin-only modules: analytics, campaigns, rules, webhooks, support
    const adminOnly = ['analytics', 'campaigns', 'rules', 'webhooks', 'support'];
    if (adminOnly.includes(m.key) && targetRole !== 'admin') return false;
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0047AB]" /> Module Permissions
          </DialogTitle>
        </DialogHeader>
        {member && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm mb-3">
            <div>
              <p className="text-sm font-medium text-slate-900">{member.name}</p>
              <p className="text-xs text-slate-500">{member.email} &middot; <span className="font-medium">{member.role}</span></p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-500">Toggle modules this user can access</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={selectAll}>All</Button>
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={clearAll}>None</Button>
              </div>
            </div>
            <div className="space-y-1.5" data-testid="module-permissions-list">
              {availableModules.map(mod => {
                const isEnabled = permissions.includes(mod.key);
                return (
                  <button
                    key={mod.key}
                    onClick={() => toggle(mod.key)}
                    className={`w-full flex items-center gap-3 p-2.5 border rounded-sm text-left transition-colors ${
                      isEnabled ? 'border-[#0047AB] bg-[#EFF6FF]' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`perm-${mod.key}`}
                  >
                    <div className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${
                      isEnabled ? 'bg-[#0047AB] border-[#0047AB]' : 'border-slate-300'
                    }`}>
                      {isEnabled && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isEnabled ? 'text-[#0047AB]' : 'text-slate-700'}`}>{mod.label}</p>
                      <p className="text-[10px] text-slate-400">{mod.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">{permissions.length} of {availableModules.length} modules enabled</p>
          </>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="save-permissions-btn">
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
